import { useEffect, useState } from 'react';
import { API_CONFIG, getApiUrl } from '@/config/apiConfig';
import {
  WIN_TICKER_INTERVAL_MS,
  buildWinTickerItemsForBucket,
  currentWinTickerBucket,
  type WinTickerItem,
} from '@/features/games/utils/winTickerDeterministic';

type WinTickerApiData = {
  bucket: number;
  intervalMs: number;
  validUntil: number;
  items: WinTickerItem[];
};

function parseWinTickerResponse(json: unknown): WinTickerApiData | null {
  const root = json as { data?: WinTickerApiData };
  const d = root?.data;
  if (!d || !Array.isArray(d.items) || d.items.length === 0) return null;
  return d;
}

/**
 * Win ticker data source: GET /kiosk/win-ticker, with the deterministic
 * client-side bucket generator as first-paint / offline fallback.
 * (Same contract previously implemented inside BottomWinTicker.)
 */
export function useWinTickerItems(): WinTickerItem[] {
  const [items, setItems] = useState<WinTickerItem[]>(() =>
    buildWinTickerItemsForBucket(currentWinTickerBucket()),
  );

  useEffect(() => {
    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;

    const scheduleNext = (validUntil: number) => {
      if (refreshTimer) clearTimeout(refreshTimer);
      const delay = Math.max(5_000, validUntil - Date.now() + 500);
      refreshTimer = setTimeout(load, delay);
    };

    const load = () => {
      fetch(getApiUrl(API_CONFIG.ENDPOINTS.KIOSK.WIN_TICKER), { credentials: 'include' })
        .then((res) => (res.ok ? res.json() : null))
        .then((json) => {
          if (cancelled || json == null) return;
          const parsed = parseWinTickerResponse(json);
          if (parsed?.items?.length) {
            setItems(parsed.items);
            scheduleNext(parsed.validUntil);
          } else {
            setItems(buildWinTickerItemsForBucket(currentWinTickerBucket()));
            scheduleNext((currentWinTickerBucket() + 1) * WIN_TICKER_INTERVAL_MS);
          }
        })
        .catch(() => {
          if (cancelled) return;
          setItems(buildWinTickerItemsForBucket(currentWinTickerBucket()));
          scheduleNext((currentWinTickerBucket() + 1) * WIN_TICKER_INTERVAL_MS);
        });
    };

    load();

    return () => {
      cancelled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, []);

  return items;
}
